import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { fetchBySlug, getLocalized, fetchCollectionV2 } from "../lib/cms";
import { SERVICES_BOXES, CONTACT_INFO, HERO_SLIDESHOW_IMAGES } from "../data";

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [featuredGalleries, setFeaturedGalleries] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);

  // Slideshow auto-play
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_SLIDESHOW_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    async function loadHomeData() {
      try {
        const data = await fetchBySlug("index");
        setPageData(data);
      } catch (err) {
        console.error("Fejl ved hentning af forside-data:", err);
      }

      try {
        const galleries = await fetchCollectionV2("gallery", { limit: 3 });
        setFeaturedGalleries(galleries.items || []);
      } catch (err) {
        console.error("Fejl ved hentning af gallerier:", err);
      } finally {
        setLoading(false);
      }
    }
    loadHomeData();
  }, []);

  const metaTitle = pageData ? getLocalized(pageData.meta_title) : "Bachmann Tømrer";
  const metaDesc = pageData ? getLocalized(pageData.meta_description) : "Bachmann Tømrer udfører professionelt tømrerarbejde med fokus på kvalitet, solidt håndværk og flotte løsninger til private og erhverv.";

  return (
    <div className="animate-in fade-in duration-500">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        {pageData?.images?.[0] && <meta property="og:image" content={pageData.images[0]} />}
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-brand-graphite text-white py-20">
        {/* Flexible Media Area (Slideshow / Video) */}
        <div className="absolute inset-0 z-0">
          {/* Indtil video modtages, vises skiftende billeder */}
          {HERO_SLIDESHOW_IMAGES.map((img, idx) => (
            <div
              key={idx}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                idx === currentSlide ? "opacity-40" : "opacity-0"
              }`}
            >
              <img
                src={`${img}?width=1600&quality=80&format=auto`}
                alt={`Bachmann Tømrer Slideshow ${idx + 1}`}
                className="h-full w-full object-cover"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-brand-graphite via-brand-graphite/50 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 rounded-none bg-brand-brown/20 border border-brand-brown/30 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-wood mb-6">
            <i className="fa-solid fa-hammer text-xs" /> Din lokale tømrermester i Silkeborg
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-white mb-6 max-w-4xl mx-auto leading-tight">
            Bachmann tømrer
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-brand-beige/90 max-w-2xl mx-auto mb-10 leading-relaxed font-sans">
            Professionelt tømrerarbejde med fokus på kvalitet, solidt håndværk og tæt dialog. Vi gør dine visioner til virkelighed.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto rounded-none bg-brand-brown px-8 py-4 text-sm font-bold uppercase tracking-wider text-white shadow-lg transition-all duration-300 hover:bg-brand-brown-dark hover:shadow-xl hover:-translate-y-0.5"
            >
              Få et tilbud
            </Link>
            <Link
              to="/referencer-2"
              className="w-full sm:w-auto rounded-none border-2 border-white/80 bg-white/10 backdrop-blur-sm px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-white hover:text-brand-graphite hover:-translate-y-0.5"
            >
              Se referencer
            </Link>
          </div>

          {/* Quick Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 border-t border-white/10 max-w-3xl mx-auto text-xs sm:text-sm text-brand-beige/80">
            <div className="flex items-center justify-center gap-2">
              <i className="fa-solid fa-phone text-brand-wood" />
              <a href={`tel:${CONTACT_INFO.phoneRaw}`} className="hover:text-white transition-colors font-semibold">
                {CONTACT_INFO.phone}
              </a>
            </div>
            <div className="flex items-center justify-center gap-2">
              <i className="fa-solid fa-envelope text-brand-wood" />
              <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-white transition-colors font-semibold">
                {CONTACT_INFO.email}
              </a>
            </div>
            <div className="flex items-center justify-center gap-2">
              <i className="fa-solid fa-location-dot text-brand-wood" />
              <span className="font-semibold">Silkeborg, Ry & Kjellerup</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Boxes Section (Ydelseskasser) - Placed high up, right after Hero */}
      <section className="py-16 bg-brand-beige-dark/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-brown">Hvad vi tilbyder</span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand-graphite mt-2">
              Vores tømrerydelser
            </h2>
            <p className="text-sm text-brand-graphite/70 mt-4">
              Vi udfører en bred vifte af tømreropgaver med fokus på kvalitet og holdbarhed. Klik på en ydelse for at læse mere.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES_BOXES.map((service, idx) => {
              const isLast = idx === SERVICES_BOXES.length - 1;
              const isOdd = SERVICES_BOXES.length % 2 !== 0;
              const isLoneLg = SERVICES_BOXES.length % 3 === 1;

              return (
                <Link
                  key={idx}
                  to={service.href}
                  className={`group relative flex flex-col overflow-hidden rounded-none border border-brand-brown/10 bg-white shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                    isLast && isOdd ? "sm:col-span-2 lg:col-span-1" : ""
                  } ${
                    isLast && isLoneLg ? "lg:col-span-3" : ""
                  }`}
                >
                  {/* Service Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={`${service.image}?width=600&quality=80&format=auto`}
                      alt={service.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-graphite/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 flex h-10 w-10 items-center justify-center rounded-none bg-brand-brown text-white shadow-md">
                      <i className={`${service.icon} text-sm`} />
                    </div>
                  </div>

                  {/* Service Content */}
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-serif text-lg font-bold text-brand-graphite group-hover:text-brand-brown transition-colors">
                      {service.title}
                    </h3>
                    <p className="mt-2 text-xs text-brand-graphite/70 leading-relaxed flex-1">
                      {service.desc}
                    </p>
                    <div className="mt-4 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-brown group-hover:text-brand-brown-dark">
                      Læs mere <i className="fa-solid fa-arrow-right text-[10px] transition-transform duration-200 group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3 Distinct Sections replacing the single intro section */}
      <>
        {/* Section 1: Professionelt Tømrerarbejde (Introduction) */}
        <section className="py-24 bg-white overflow-hidden">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Left Column: Text */}
              <div className="space-y-6">
                <span className="text-xs font-bold uppercase tracking-widest text-brand-brown">
                  Kvalitet & ordentlighed
                </span>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-graphite leading-tight tracking-tight">
                  Tømrer – professionelt tømrerarbejde til dit hjem
                </h2>
                <div className="space-y-4 text-brand-graphite/80 leading-relaxed text-sm sm:text-base">
                  <p>
                    Hos Bachmann Tømrer forstår jeg, at dit hjem og dine projekter fortjener det bedste. Med årtiers erfaring leverer jeg et solidt håndværk og skræddersyede løsninger, der matcher dine ønsker og behov – hver gang!
                  </p>
                  <p>
                    Jeg holder til i Silkeborg og kører i Ry, Kjellerup og hele Midt- og Østjylland. Uanset om du planlægger en mindre renovering, en tilbygning eller har brug for akut tømrerhjælp, kan du få solide og holdbare løsninger. Jeg arbejder tæt sammen med dig, så du får præcis det resultat, du ønsker, og du kan følge processen fra start til slut.
                  </p>
                </div>
              </div>

              {/* Right Column: Image */}
              <div className="overflow-hidden border border-brand-brown/10 shadow-lg">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/4308d28a-24e2-4c25-a37f-97d46b6f1512/img-6844-754x1000.jpeg"
                  alt="Moderne hus opført af Bachmann Tømrer"
                  className="w-full h-[350px] sm:h-[480px] object-cover transition-transform duration-500 hover:scale-102"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Kvalitet & Håndværk (Our Values) */}
        <section className="py-24 bg-brand-beige/20 border-t border-b border-brand-brown/5 overflow-hidden">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Left Column: Image */}
              <div className="order-2 lg:order-1 overflow-hidden border border-brand-brown/10 shadow-lg">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/4308d28a-24e2-4c25-a37f-97d46b6f1512/img-6834-752x1000.jpeg"
                  alt="Kvalitetsbyggeri og detaljer"
                  className="w-full h-[350px] sm:h-[480px] object-cover transition-transform duration-500 hover:scale-102"
                />
              </div>

              {/* Right Column: Text */}
              <div className="order-1 lg:order-2 space-y-6">
                <span className="text-xs font-bold uppercase tracking-widest text-brand-brown">
                  Vores værdier
                </span>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand-graphite leading-tight tracking-tight">
                  Kvalitet & tryghed i højsædet
                </h2>
                <p className="text-brand-graphite/80 text-sm sm:text-base leading-relaxed">
                  Som tømrerfirma og tømrermester har jeg many års erfaring med både private hjem og erhvervsejendomme. Mit fokus er altid på kvalitet, skræddersyede løsninger og tæt dialog, så du trygt kan overlade dit projekt til os.
                </p>

                {/* Bulleted list with checkmark icons */}
                <ul className="space-y-4 text-sm sm:text-base text-brand-graphite/80">
                  <li className="flex items-start gap-3">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-none bg-brand-brown/10 text-brand-brown mt-0.5">
                      <i className="fa-solid fa-check text-xs" />
                    </span>
                    <span className="font-medium">Solidt dansk håndværk med sans for detaljen</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-none bg-brand-brown/10 text-brand-brown mt-0.5">
                      <i className="fa-solid fa-check text-xs" />
                    </span>
                    <span className="font-medium">Lokalt nærvær og tryghed gennem hele forløbet</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-none bg-brand-brown/10 text-brand-brown mt-0.5">
                      <i className="fa-solid fa-check text-xs" />
                    </span>
                    <span className="font-medium">Gennemskuelige aftaler og ordentlig oprydning</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-none bg-brand-brown/10 text-brand-brown mt-0.5">
                      <i className="fa-solid fa-check text-xs" />
                    </span>
                    <span className="font-medium">Tæt dialog og rådgivning fra start til slut</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Skræddersyede Løsninger (Custom Solutions & CTA) */}
        <section className="py-24 bg-white overflow-hidden">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Left Column: Text */}
              <div className="space-y-6">
                <span className="text-xs font-bold uppercase tracking-widest text-brand-brown">
                  Få et tilbud
                </span>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand-graphite leading-tight tracking-tight">
                  Lad os realisere dine drømme
                </h2>
                <p className="text-brand-graphite/80 text-sm sm:text-base leading-relaxed">
                  Uanset om du skal have lagt nyt tag, opført en træterrasse, udskiftet døre og vinduer eller bygget en helt ny tilbygning, står vi klar til at hjælpe dig sikkert i mål. Vi rådgiver dig professionelt, så du får den helt rigtige løsning.
                </p>
                
                {/* Primary CTA outlined button */}
                <div className="pt-4">
                  <Link
                    to="/kontakt"
                    className="inline-block border-2 border-brand-graphite text-brand-graphite hover:bg-brand-graphite hover:text-white px-8 py-3.5 text-xs font-bold uppercase tracking-wider transition-all duration-300"
                  >
                    FÅ ET UFORPLIGTENDE TILBUD
                  </Link>
                </div>
              </div>

              {/* Right Column: Image */}
              <div className="overflow-hidden border border-brand-brown/10 shadow-lg">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/4308d28a-24e2-4c25-a37f-97d46b6f1512/img-6847-748x1000.jpeg"
                  alt="Tilbygning udført af Bachmann Tømrer"
                  className="w-full h-[350px] sm:h-[480px] object-cover transition-transform duration-500 hover:scale-102"
                />
              </div>
            </div>
          </div>
        </section>
      </>

      {/* Featured References / Gallery Section */}
      <section className="py-20 bg-brand-beige-dark/20 border-t border-brand-brown/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-end justify-between mb-12 gap-4 text-center sm:text-left">
            <div className="w-full sm:w-auto">
              <span className="text-xs font-bold uppercase tracking-widest text-brand-brown block">Vores arbejde</span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand-graphite mt-2">
                Udvalgte projekter
              </h2>
            </div>
            <Link
              to="/galleri"
              className="inline-flex items-center gap-2 rounded-none border border-brand-brown/30 bg-white px-6 py-3 text-xs font-bold uppercase tracking-wider text-brand-brown shadow-sm transition-all duration-300 hover:bg-brand-beige"
            >
              Se hele galleriet <i className="fa-solid fa-arrow-right text-[10px]" />
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {featuredGalleries.map((gallery, idx) => (
              <Link
                key={idx}
                to="/galleri"
                className="group relative flex flex-col overflow-hidden rounded-none border border-brand-brown/10 bg-white shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={`${gallery.images?.[0]}?width=600&quality=80&format=auto`}
                    alt={getLocalized(gallery.title)}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-graphite/80 via-brand-graphite/20 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-block rounded-none bg-brand-brown/90 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white mb-2">
                      {getLocalized(gallery.category)}
                    </span>
                    <h3 className="font-serif text-lg font-bold text-white leading-tight">
                      {getLocalized(gallery.title)}
                    </h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-28 bg-brand-graphite text-white overflow-hidden">
        {/* Parallax Background Image */}
        <div 
          className="absolute inset-0 bg-fixed bg-cover bg-center opacity-30"
          style={{ 
            backgroundImage: `url('https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/4308d28a-24e2-4c25-a37f-97d46b6f1512/image00038-scaled.jpeg')` 
          }}
        />
        {/* Dark Overlay to ensure high contrast */}
        <div className="absolute inset-0 bg-brand-graphite/60" />
        
        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-6 text-white">
            Skal vi hjælpe dig med dit næste projekt?
          </h2>
          <p className="text-base sm:text-lg text-brand-beige/90 max-w-2xl mx-auto mb-10 leading-relaxed">
            Uanset om det drejer sig om et nyt tag, en træterrasse, udskiftning af vinduer eller en totalrenovering, står vi klar med professionel rådgivning og et uforpligtende tilbud.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto rounded-none bg-white px-8 py-4 text-sm font-bold uppercase tracking-wider text-brand-brown shadow-lg transition-all duration-300 hover:bg-brand-beige hover:shadow-xl hover:-translate-y-0.5"
            >
              Få et uforpligtende tilbud
            </Link>
            <a
              href={`tel:${CONTACT_INFO.phoneRaw}`}
              className="w-full sm:w-auto rounded-none border-2 border-white px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-white/10 hover:-translate-y-0.5"
            >
              <i className="fa-solid fa-phone mr-2" /> Ring til os nu
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
